import React, { useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Box, CssBaseline } from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'

// Layout
import AppLayout from './components/layout/AppLayout'
import ProtectedRoute from './components/ProtectedRoute'
import { ROUTE_ROLES } from './config/routeRoles'

// Page Components
import AdminDashboard from './pages/AdminDashboard'
import TLDashboard from './pages/TLDashboard'
import TrainerDashboard from './pages/TrainerDashboard'
import Schools from './pages/Schools'
import SchoolSearch from './pages/SchoolSearch'
import DemoSchools from './pages/DemoSchools'
import BilledSchools from './pages/BilledSchools'
import NotBilledSchools from './pages/NotBilledSchools'
import ExpiredSchools from './pages/ExpiredSchools'
import SchoolDetails from './pages/SchoolDetails'
import Training from './pages/Training'
import SOP from './pages/SOP'
import Announcements from './pages/Announcements'
import BillDesk from './pages/BillDesk'
import ProductList from './pages/ProductList'
import Analytics from './pages/Analytics'
import Contracts from './pages/Contracts'
import Renewals from './pages/Renewals'
import Support from './pages/Support'
import SupportChatInbox from './pages/SupportChatInbox'
import Settings from './pages/Settings'
import Login from './pages/Login'
import WhatsAppMessaging from './pages/WhatsAppMessaging'
import WhatsAppQueue from './pages/WhatsAppQueue'
import WhatsAppInbox from './pages/WhatsAppInbox'
import ManageWhatsAppAccounts from './pages/ManageWhatsAppAccounts'
import WhatsAppPricePlansPage from './pages/WhatsAppPricePlansPage'
import WhatsAppTemplatesPage from './pages/WhatsAppTemplatesPage'
import WhatsAppReportsPage from './pages/WhatsAppReportsPage'
import WhatsAppAuditLogsPage from './pages/WhatsAppAuditLogsPage'
import SchoolAccounts from './pages/SchoolAccounts'
import LoginLogs from './pages/LoginLogs'
import SchoolQuery from './pages/SchoolQuery'
import SchoolGroups from './pages/SchoolGroups'
import TelecallerManagement from './pages/TelecallerManagement'
import AIAgents from './pages/AIAgents'
import ReferralManagement from './pages/ReferralManagement'
import PromoterManagement from './pages/PromoterManagement'
import Validation from './pages/Validation'
import TransactionPanel from './pages/TransactionPanel'
import ManageWebsite from './pages/ManageWebsite'
import ManageWebsiteCreate from './pages/ManageWebsiteCreate'
import ManageWebsiteSchoolDetail from './pages/ManageWebsiteSchoolDetail'

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { WhatsAppEventsProvider } from './contexts/WhatsAppEventsContext'

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(true)

  // Wait for auth check to complete
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '1.2rem',
          color: '#666',
        }}
      >
        Loading...
      </Box>
    )
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  const toggleSidebar = () => setSidebarOpen((prev) => !prev)

  // Wraps a page in the shared route-transition animation, then in a role
  // guard for the given path (see src/config/routeRoles.js). Routes not
  // present in ROUTE_ROLES are open to any authenticated role.
  const guardedRoute = (path, PageComponent) => (
    <ProtectedRoute roles={ROUTE_ROLES[path]}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <PageComponent />
      </motion.div>
    </ProtectedRoute>
  )

  return (
    <>
      <CssBaseline />
      <WhatsAppEventsProvider enabled={isAuthenticated}>
        <AppLayout sidebarOpen={sidebarOpen} onSidebarToggle={toggleSidebar}>
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/dashboard" element={guardedRoute('/dashboard', AdminDashboard)} />
              <Route path="/dashboard/tl" element={guardedRoute('/dashboard/tl', TLDashboard)} />
              <Route path="/dashboard/trainer" element={guardedRoute('/dashboard/trainer', TrainerDashboard)} />
              <Route path="/schools" element={guardedRoute('/schools', Schools)} />
              <Route path="/schools/search" element={guardedRoute('/schools/search', SchoolSearch)} />
              <Route path="/schools/demo" element={guardedRoute('/schools/demo', DemoSchools)} />
              <Route path="/schools/billed" element={guardedRoute('/schools/billed', BilledSchools)} />
              <Route path="/schools/not-billed" element={guardedRoute('/schools/not-billed', NotBilledSchools)} />
              <Route path="/schools/expired" element={guardedRoute('/schools/expired', ExpiredSchools)} />
              <Route path="/schools/detail/:schoolCode" element={guardedRoute('/schools/detail', SchoolDetails)} />
              <Route path="/training" element={guardedRoute('/training', Training)} />
              <Route path="/sop" element={guardedRoute('/sop', SOP)} />
              <Route path="/announcements" element={guardedRoute('/announcements', Announcements)} />
              <Route path="/product-list" element={guardedRoute('/product-list', ProductList)} />
              <Route path="/billdesk" element={guardedRoute('/billdesk', BillDesk)} />
              <Route path="/transaction-panel" element={guardedRoute('/transaction-panel', TransactionPanel)} />
              <Route path="/analytics" element={guardedRoute('/analytics', Analytics)} />
              <Route path="/contracts" element={guardedRoute('/contracts', Contracts)} />
              <Route path="/renewals" element={guardedRoute('/renewals', Renewals)} />
              <Route path="/support" element={guardedRoute('/support', Support)} />
              <Route path="/support-chat" element={guardedRoute('/support-chat', SupportChatInbox)} />
              <Route path="/settings" element={guardedRoute('/settings', Settings)} />
              <Route path="/whatsapp" element={guardedRoute('/whatsapp', WhatsAppMessaging)} />
              <Route path="/whatsapp/queue" element={guardedRoute('/whatsapp/queue', WhatsAppQueue)} />
              <Route path="/whatsapp/inbox" element={guardedRoute('/whatsapp/inbox', WhatsAppInbox)} />
              <Route path="/whatsapp/accounts" element={guardedRoute('/whatsapp/accounts', ManageWhatsAppAccounts)} />
              <Route path="/whatsapp/price-plans" element={guardedRoute('/whatsapp/price-plans', WhatsAppPricePlansPage)} />
              <Route path="/whatsapp/templates" element={guardedRoute('/whatsapp/templates', WhatsAppTemplatesPage)} />
              <Route path="/whatsapp/templates/master" element={guardedRoute('/whatsapp/templates/master', WhatsAppTemplatesPage)} />
              <Route path="/whatsapp/reports" element={guardedRoute('/whatsapp/reports', WhatsAppReportsPage)} />
              <Route path="/whatsapp/audit" element={guardedRoute('/whatsapp/audit', WhatsAppAuditLogsPage)} />
              <Route path="/whatsapp/manage" element={<Navigate to="/whatsapp/accounts" replace />} />
              <Route path="/whatsapp/embedded-signup" element={<Navigate to="/whatsapp/accounts" replace />} />
              <Route path="/school-accounts" element={guardedRoute('/school-accounts', SchoolAccounts)} />
              <Route path="/login-logs" element={guardedRoute('/login-logs', LoginLogs)} />
              <Route path="/my-orders" element={<Navigate to="/billdesk" replace />} />
              <Route path="/school-query" element={guardedRoute('/school-query', SchoolQuery)} />
              <Route path="/school-groups" element={guardedRoute('/school-groups', SchoolGroups)} />
              <Route path="/telecaller" element={guardedRoute('/telecaller', TelecallerManagement)} />
              <Route path="/ai-agents" element={guardedRoute('/ai-agents', AIAgents)} />
              <Route path="/referrals" element={guardedRoute('/referrals', ReferralManagement)} />
              <Route path="/promoters" element={guardedRoute('/promoters', PromoterManagement)} />
              <Route path="/validation" element={guardedRoute('/validation', Validation)} />
              <Route path="/manage-website" element={guardedRoute('/manage-website', ManageWebsite)} />
              <Route path="/manage-website/create" element={guardedRoute('/manage-website', ManageWebsiteCreate)} />
              <Route path="/manage-website/:schoolCode" element={guardedRoute('/manage-website', ManageWebsiteSchoolDetail)} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AnimatePresence>
        </AppLayout>
      </WhatsAppEventsProvider>
    </>
  )
}

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
